import { detectCurrencyFromCountry, resolveVisitorCountry } from './geo';

export const CURRENCY_OPTIONS = [
  { code: 'USD', symbol: '$', name: 'US Dollar', decimals: 2, locale: 'en-US' },
  { code: 'HTG', symbol: 'G', name: 'Gourde', decimals: 0, locale: 'fr-HT' },
  { code: 'XOF', symbol: 'CFA', name: 'FCFA', decimals: 0, locale: 'fr-SN' },
  { code: 'EUR', symbol: '€', name: 'Euro', decimals: 2, locale: 'fr-FR' },
  { code: 'GBP', symbol: '£', name: 'Pound', decimals: 2, locale: 'en-GB' },
  { code: 'BRL', symbol: 'R$', name: 'Real', decimals: 2, locale: 'pt-BR' },
  { code: 'CNY', symbol: '¥', name: 'Yuan', decimals: 2, locale: 'zh-CN' },
];

export const SUPPORTED_CURRENCIES = CURRENCY_OPTIONS.map((item) => item.code);

// Fallback defaults before /api/settings loads; HTG always comes from admin NatCash rate.
const DEFAULT_RATES = {
  USD: 1,
  HTG: 135,
  XOF: 600,
  EUR: 0.92,
  GBP: 0.79,
  BRL: 5.0,
  CNY: 7.25,
};

const EURO_TIMEZONES = new Set([
  'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome', 'Europe/Brussels',
  'Europe/Amsterdam', 'Europe/Lisbon', 'Europe/Vienna', 'Europe/Dublin',
]);

const FCFA_TIMEZONES = new Set([
  'Africa/Abidjan', 'Africa/Dakar', 'Africa/Bamako', 'Africa/Ouagadougou',
  'Africa/Lome', 'Africa/Porto-Novo', 'Africa/Niamey', 'Africa/Conakry',
]);

const isSupportedCurrency = (code) => SUPPORTED_CURRENCIES.includes(code);

export const hasManualCurrencyPreference = () => {
  try {
    return localStorage.getItem('currencyManual') === '1';
  } catch {
    return false;
  }
};

export const markCurrencyPreferenceManual = (code) => {
  try {
    localStorage.setItem('currencyManual', '1');
    localStorage.setItem('displayCurrency', code);
  } catch {
    // ignore
  }
};

export const buildRatesFromSettings = (settings) => {
  const htg = Number(settings?.natcash_usd_htg_rate) || DEFAULT_RATES.HTG;
  const stored = settings?.currency_rates || {};
  const rates = { ...DEFAULT_RATES, HTG: htg };
  for (const [code, value] of Object.entries(stored)) {
    const normalized = String(code || '').toUpperCase();
    const num = Number(value);
    if (normalized && Number.isFinite(num) && num > 0) {
      rates[normalized] = num;
    }
  }
  rates.USD = 1;
  rates.HTG = htg;
  return rates;
};

const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

const getBrowserRegions = () => {
  const regions = [];
  if (typeof navigator !== 'undefined') {
    if (navigator.language) regions.push(navigator.language);
    if (Array.isArray(navigator.languages)) regions.push(...navigator.languages);
  }
  return regions.map((raw) => String(raw || '').toLowerCase());
};

const matchesHaitianLocale = (locale) => {
  const normalized = String(locale || '').toLowerCase();
  return normalized === 'ht' || normalized.startsWith('ht-') || normalized.endsWith('-ht');
};

export const detectCurrencyFromEnvironment = (options = {}) => {
  const country = resolveVisitorCountry(options);
  const fromCountry = country ? detectCurrencyFromCountry(country) : null;
  if (fromCountry) return fromCountry;

  const timezone = getTimezone();
  if (timezone === 'America/Port-au-Prince') return 'HTG';
  if (timezone === 'America/Sao_Paulo' || timezone.startsWith('America/Belem')) return 'BRL';
  if (timezone === 'Asia/Shanghai' || timezone === 'Asia/Hong_Kong' || timezone === 'Asia/Taipei') return 'CNY';
  if (timezone === 'Europe/London') return 'GBP';
  if (EURO_TIMEZONES.has(timezone)) return 'EUR';
  if (FCFA_TIMEZONES.has(timezone)) return 'XOF';

  for (const locale of getBrowserRegions()) {
    if (matchesHaitianLocale(locale)) return 'HTG';
    if (locale.includes('pt-br') || locale.endsWith('-br')) return 'BRL';
    if (locale.startsWith('zh')) return 'CNY';
    if (locale.endsWith('-gb')) return 'GBP';
    if (locale.endsWith('-sn') || locale.endsWith('-ci') || locale.endsWith('-ml')) return 'XOF';
    if (locale.endsWith('-fr') && !locale.endsWith('-ht')) return 'EUR';
  }
  return 'USD';
};

export const getInitialCurrency = (settings, options = {}) => {
  if (hasManualCurrencyPreference()) {
    try {
      const saved = localStorage.getItem('displayCurrency');
      if (isSupportedCurrency(saved)) return saved;
    } catch {
      // ignore
    }
  }
  // Avoid HTG flash (and stale 135 rate) before settings + geo have loaded.
  if (!options?.geoResolved) {
    return 'USD';
  }
  const rates = buildRatesFromSettings(settings);
  const detected = detectCurrencyFromEnvironment(options);
  return rates[detected] ? detected : 'USD';
};

export const getCurrencyMeta = (code) => (
  CURRENCY_OPTIONS.find((item) => item.code === code) || CURRENCY_OPTIONS[0]
);

export const convertUsdAmount = (usdAmount, currencyCode, rates) => {
  const amount = Number(usdAmount);
  if (!Number.isFinite(amount)) return 0;
  const rate = Number(rates?.[currencyCode] || rates?.USD || 1);
  if (currencyCode === 'USD') return amount;
  return amount * rate;
};

export const formatMoney = (usdAmount, currencyCode, rates, options = {}) => {
  const meta = getCurrencyMeta(currencyCode);
  const converted = convertUsdAmount(usdAmount, currencyCode, rates);
  const decimals = options.decimals ?? meta.decimals;
  try {
    if (currencyCode === 'HTG') {
      return `G ${Math.round(converted).toLocaleString(meta.locale)}`;
    }
    if (currencyCode === 'XOF') {
      return `${Math.round(converted).toLocaleString('fr-FR')} CFA`;
    }
    return new Intl.NumberFormat(meta.locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(converted);
  } catch {
    return `${meta.symbol}${converted.toFixed(decimals)}`;
  }
};
